import { useState, useRef } from "react";
import { api, API } from "../lib/api";
import { Upload, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable file uploader. Auth-only.
 * scope: one of vendor_quote_attachment | hse_cert | rfp_reference | awarded_contract | library
 * scopeId: optional (quote_id / rfp_id / srfq_id)
 * showCertFields: when scope === "hse_cert", surfaces cert_type + expiry inputs
 */
export default function FileUploader({ scope, scopeId, showCertFields, onUploaded, label, compact }) {
  const [busy, setBusy] = useState(false);
  const [certType, setCertType] = useState("");
  const [expiry, setExpiry] = useState("");
  const inputRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("scope", scope);
    if (scopeId) fd.append("scope_id", scopeId);
    if (showCertFields && certType) fd.append("cert_type", certType);
    if (showCertFields && expiry) fd.append("expiry_date", expiry);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      onUploaded?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  if (compact) {
    return (
      <>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`file-upload-${scope}`}
          className="btn-tactile inline-flex items-center gap-2 text-xs"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
          {label || "Attach"}
        </button>
        <input ref={inputRef} type="file" hidden onChange={e => upload(e.target.files?.[0])} />
      </>
    );
  }

  return (
    <div className="tray-inset p-4 space-y-3" data-testid={`uploader-${scope}`}>
      {showCertFields && (
        <div className="grid sm:grid-cols-2 gap-2">
          <select
            value={certType} onChange={e => setCertType(e.target.value)}
            data-testid="cert-type-select"
            className="tray-inset px-3 py-2 bg-transparent text-sm font-mono-data outline-none focus:ring-1 focus:ring-amber-500"
          >
            <option value="" className="bg-black">Cert type…</option>
            {["OSHA", "API", "ANSI", "NACE", "ASME", "ISO", "OPITO", "Other"].map(c => (
              <option key={c} value={c} className="bg-black">{c}</option>
            ))}
          </select>
          <input
            type="date" value={expiry} onChange={e => setExpiry(e.target.value)}
            data-testid="cert-expiry-input"
            placeholder="Expiry date"
            className="tray-inset px-3 py-2 bg-transparent text-sm font-mono-data outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
        onDragOver={e => e.preventDefault()}
        data-testid={`dropzone-${scope}`}
        className="border-2 border-dashed border-white/10 hover:border-amber-500/50 rounded-lg p-6 text-center cursor-pointer transition"
      >
        {busy ? (
          <div className="inline-flex items-center gap-2 font-mono-data text-xs">
            <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> Uploading...
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-text-secondary/60 mx-auto mb-2" />
            <div className="font-display text-sm">{label || "Drop file or click to upload"}</div>
            <div className="font-mono-data text-[10px] text-text-secondary tracking-widest uppercase mt-1">
              Max 25 MB · PDF · IMG · XLSX · DOCX
            </div>
          </>
        )}
        <input ref={inputRef} type="file" hidden onChange={e => upload(e.target.files?.[0])} />
      </div>
    </div>
  );
}

export const fileDownloadUrl = (fileId) => `${API}/files/${fileId}/download`;
